import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  Animated,
  LayoutChangeEvent,
  Dimensions,
} from 'react-native';
import { createStyles } from './DbScrollTab.styles';
import type { DbScrollTabProps, TabItem } from './DbScrollTab.types';

interface TabLayout {
  x: number;
  width: number;
  textWidth: number;
}

const SCREEN_WIDTH = Dimensions.get('window').width;

const DbScrollTab: React.FC<DbScrollTabProps> = ({
  tabs,
  activeKey,
  defaultActiveKey,
  onChange,
  align = 'left',
  showIndicator = true,
  indicatorWidth = 'auto',
  indicatorHeight = 2,
  indicatorColor,
  indicatorRadius = 1,
  tabGap = 0,
  horizontalPadding = 16,
  style,
  tabStyle,
  tabTextStyle,
  activeTabTextStyle,
  disabledTabTextStyle,
  animated = true,
  scrollable = true,
  renderTab: customRenderTab,
  scrollToLeft = true,
  scrollLeftOffset = 16,
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  const scrollViewRef = useRef<ScrollView>(null);
  const containerRef = useRef<View>(null);
  const [currentKey, setCurrentKey] = useState(activeKey || defaultActiveKey || tabs[0]?.key);
  const [tabLayouts, setTabLayouts] = useState<Record<string, TabLayout>>({});
  const [containerWidth, setContainerWidth] = useState(SCREEN_WIDTH);
  const indicatorAnim = useRef(new Animated.Value(0)).current;
  const indicatorWidthAnim = useRef(new Animated.Value(0)).current;
  const [layoutReady, setLayoutReady] = useState(false);

  // 受控模式
  useEffect(() => {
    if (activeKey !== undefined) {
      setCurrentKey(activeKey);
    }
  }, [activeKey]);

  // 检查所有 tab 是否都已布局完成
  useEffect(() => {
    const allLayouted = tabs.every(tab => tabLayouts[tab.key]);
    if (allLayouted && tabs.length > 0) {
      setLayoutReady(true);
    }
  }, [tabLayouts, tabs]);

  // 更新指示器位置
  useEffect(() => {
    if (!layoutReady || !showIndicator) return;
    
    const layout = tabLayouts[currentKey];
    if (!layout) return;

    // 计算指示器宽度
    const targetWidth = indicatorWidth === 'auto' 
      ? Math.max(layout.textWidth, 20) 
      : indicatorWidth;
    
    // 计算指示器X位置 - 居中于tab项
    const targetX = layout.x + (layout.width - targetWidth) / 2;

    if (animated) {
      Animated.parallel([
        Animated.spring(indicatorAnim, {
          toValue: targetX,
          useNativeDriver: false,
          tension: 100,
          friction: 15,
        }),
        Animated.spring(indicatorWidthAnim, {
          toValue: targetWidth,
          useNativeDriver: false,
          tension: 100,
          friction: 15,
        }),
      ]).start();
    } else {
      indicatorAnim.setValue(targetX);
      indicatorWidthAnim.setValue(targetWidth);
    }

    // 滚动到可见区域
    if (scrollable && scrollViewRef.current) {
      if (scrollToLeft) {
        // 选中项靠左对齐
        scrollViewRef.current.scrollTo({
          x: Math.max(0, layout.x - scrollLeftOffset),
          animated: true,
        });
      } else {
        // 居中显示
        const scrollX = layout.x - (containerWidth / 2) + (layout.width / 2);
        scrollViewRef.current.scrollTo({
          x: Math.max(0, scrollX),
          animated: true,
        });
      }
    }
  }, [currentKey, tabLayouts, showIndicator, indicatorWidth, animated, scrollable, layoutReady, scrollToLeft, scrollLeftOffset, containerWidth]);

  const handleTabPress = useCallback((item: TabItem, index: number) => {
    if (item.disabled) return;
    
    if (activeKey === undefined) {
      setCurrentKey(item.key);
    }
    onChange?.(item.key, index);
  }, [activeKey, onChange]);

  const handleTabLayout = useCallback((key: string, x: number, width: number, textWidth: number) => {
    setTabLayouts(prev => {
      // 避免不必要的更新
      if (prev[key]?.x === x && prev[key]?.width === width && prev[key]?.textWidth === textWidth) {
        return prev;
      }
      return {
        ...prev,
        [key]: { x, width, textWidth },
      };
    });
  }, []);

  const handleContainerLayout = useCallback((e: LayoutChangeEvent) => {
    setContainerWidth(e.nativeEvent.layout.width);
  }, []);

  const renderTabItem = (item: TabItem, index: number) => {
    const isActive = currentKey === item.key;
    const isDisabled = item.disabled ?? false;

    return (
      <TabItemComponent
        key={item.key}
        item={item}
        index={index}
        isActive={isActive}
        isDisabled={isDisabled}
        tabGap={tabGap}
        align={align}
        scrollable={scrollable}
        tabsLength={tabs.length}
        styles={styles}
        tabStyle={tabStyle}
        tabTextStyle={tabTextStyle}
        activeTabTextStyle={activeTabTextStyle}
        disabledTabTextStyle={disabledTabTextStyle}
        onPress={handleTabPress}
        onLayout={handleTabLayout}
        customRender={customRenderTab}
      />
    );
  };

  const containerStyle = [
    styles.container,
    style,
  ];

  const contentStyle = [
    styles.scrollContent,
    { paddingHorizontal: horizontalPadding },
    !scrollable && { flex: 1 },
    align === 'center' && scrollable && { justifyContent: 'center' as const },
    align === 'space-between' && !scrollable && { justifyContent: 'space-between' as const },
  ];

  return (
    <View 
      ref={containerRef}
      style={containerStyle}
      onLayout={handleContainerLayout}
    >
      <ScrollView
        ref={scrollViewRef}
        horizontal
        showsHorizontalScrollIndicator={false}
        scrollEnabled={scrollable}
        style={styles.scrollView}
        contentContainerStyle={contentStyle}
      >
        {tabs.map(renderTabItem)}
        
        {showIndicator && layoutReady && (
          <Animated.View
            style={[
              styles.indicator,
              {
                left: indicatorAnim,
                width: indicatorWidthAnim,
                height: indicatorHeight,
                borderRadius: indicatorRadius,
                backgroundColor: indicatorColor || styles.indicator.backgroundColor,
              },
            ]}
          />
        )}
      </ScrollView>
    </View>
  );
};

// 单个Tab项组件
interface TabItemComponentProps {
  item: TabItem;
  index: number;
  isActive: boolean;
  isDisabled: boolean;
  tabGap: number;
  align: 'left' | 'center' | 'space-between';
  scrollable: boolean;
  tabsLength: number;
  styles: ReturnType<typeof createStyles>;
  tabStyle?: DbScrollTabProps['tabStyle'];
  tabTextStyle?: DbScrollTabProps['tabTextStyle'];
  activeTabTextStyle?: DbScrollTabProps['activeTabTextStyle'];
  disabledTabTextStyle?: DbScrollTabProps['disabledTabTextStyle'];
  onPress: (item: TabItem, index: number) => void;
  onLayout: (key: string, x: number, width: number, textWidth: number) => void;
  customRender?: DbScrollTabProps['renderTab'];
}

const TabItemComponent: React.FC<TabItemComponentProps> = ({
  item,
  index,
  isActive,
  isDisabled,
  tabGap,
  align,
  scrollable,
  tabsLength,
  styles,
  tabStyle,
  tabTextStyle,
  activeTabTextStyle,
  disabledTabTextStyle,
  onPress,
  onLayout,
  customRender,
}) => {
  const containerRef = useRef<View>(null);
  const [textWidth, setTextWidth] = useState(0);
  const [itemLayout, setItemLayout] = useState<{ x: number; width: number } | null>(null);

  const handleTextLayout = useCallback((e: LayoutChangeEvent) => {
    const width = e.nativeEvent.layout.width;
    setTextWidth(width);
  }, []);

  const handleLayout = useCallback((e: LayoutChangeEvent) => {
    const { x, width } = e.nativeEvent.layout;
    setItemLayout({ x, width });
  }, []);

  // 当布局和文字宽度都获取到后，通知父组件
  useEffect(() => {
    if (itemLayout && textWidth > 0) {
      onLayout(item.key, itemLayout.x, itemLayout.width, textWidth);
    } else if (itemLayout && customRender) {
      // 自定义渲染时，使用容器宽度的一部分作为指示器宽度
      onLayout(item.key, itemLayout.x, itemLayout.width, itemLayout.width * 0.6);
    }
  }, [itemLayout, textWidth, item.key, onLayout, customRender]);

  const itemStyle = [
    styles.tabItem,
    align === 'center' && styles.tabItemCenter,
    !scrollable && { flex: 1 },
    index > 0 && tabGap > 0 && { marginLeft: tabGap },
    tabStyle,
  ];

  const textStyle = [
    styles.tabText,
    tabTextStyle,
    isActive && styles.activeTabText,
    isActive && activeTabTextStyle,
    isDisabled && styles.disabledTabText,
    isDisabled && disabledTabTextStyle,
  ];

  // 自定义渲染
  if (customRender) {
    return (
      <TouchableOpacity
        activeOpacity={isDisabled ? 1 : 0.7}
        onPress={() => onPress(item, index)}
        onLayout={handleLayout}
        style={itemStyle}
      >
        {customRender(item, isActive, index)}
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      activeOpacity={isDisabled ? 1 : 0.7}
      onPress={() => onPress(item, index)}
      onLayout={handleLayout}
      style={itemStyle}
    >
      {item.icon && (
        <View style={styles.tabIcon}>
          {item.icon}
        </View>
      )}
      {item.title && (
        <Text
          style={textStyle}
          onLayout={handleTextLayout}
        >
          {item.title}
        </Text>
      )}
      
      {item.badge !== undefined && item.badge > 0 && (
        <View style={styles.badge}>
          <Text style={styles.badgeText}>
            {item.badge > 99 ? '99+' : item.badge}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );
};

export default DbScrollTab;
export type { DbScrollTabProps, TabItem };
